import { Component, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { SidebarService } from '../service/sidebar/sidebar.service';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { isCapacitorNative } from '../service/platform/capacitor-platform';

/**
 * Mounted once globally from `app.component.html`. Both the dashboard layout
 * header and the shared `<app-navbar>` open the same drawer through
 * `SidebarService`, so the navigation menu is reachable from every authed
 * route — including the four tab destinations (New, Saved, Shared, Search)
 * which previously had no entry into the user menu.
 *
 * The drawer fetches its own message-unread count and re-checks every 60s.
 * Pre-refactor that logic lived inside `DashboardLayoutComponent` and only
 * fired on the dashboard. Lifting it here keeps the Messages badge accurate
 * regardless of which screen the user is on when they open the drawer.
 */
@Component({
  selector: 'app-sidebar-drawer',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar-drawer.component.html',
  styleUrl: './sidebar-drawer.component.css',
})
export class SidebarDrawerComponent implements OnInit, OnDestroy {
  private readonly sidebar = inject(SidebarService);
  private readonly auth = inject(AuthService);
  private readonly rest = inject(RestService);
  private readonly router = inject(Router);

  readonly isOpen = this.sidebar.isOpen;

  /** Admin entry is web-only. The Capacitor build excludes admin routes
   *  at compile time, so showing a dead link in the mobile drawer would
   *  just send users to /dashboard. Hide it there. */
  readonly isAdmin = computed(
    () => !isCapacitorNative() && !!this.auth.getUser()?.admin,
  );

  unreadCount = 0;
  messagingEnabled = false;

  private routerSub?: Subscription;
  private refreshTimer?: ReturnType<typeof setInterval>;

  async ngOnInit(): Promise<void> {
    await this.loadUnreadCount();
    this.refreshTimer = setInterval(() => this.loadUnreadCount(), 60_000);

    // Auto-close on every successful navigation. Without this the drawer
    // would stay open after a tap because clicking the same `routerLink`
    // for the current route does not emit NavigationEnd elsewhere — moved
    // here so the rule is enforced regardless of which screen opened it.
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.sidebar.close());
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  close(): void {
    this.sidebar.close();
  }

  onBackdropKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') this.close();
  }

  private async loadUnreadCount(): Promise<void> {
    const sid = this.auth.getUser()?.sid;
    if (!sid) {
      this.unreadCount = 0;
      return;
    }
    try {
      const config = await this.rest.getContactConfig(sid);
      this.messagingEnabled = config?.contactEnabled === true;
      this.unreadCount = this.messagingEnabled ? (config?.unreadCount ?? 0) : 0;
    } catch {
      // Network errors shouldn't tear down the drawer; keep last known count.
    }
  }
}
